import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { api, isApiError, type KbScopeCheck, type KbSharepointDrive, type KbSourceRecord } from '../lib/api'

/** What the form collects. Mirrors the record minus the fields the server owns. */
export interface KbSourceDraft {
  id: string
  name: string
  siteUrl: string
  resourceId: string | null
  enabled: boolean
  accessible: boolean
  displayAlias: string
  folders: string[]
  excludeFolders: string[]
}

function draftFrom(src: KbSourceRecord | null): KbSourceDraft {
  return {
    id: src?.id ?? '',
    name: src?.name ?? '',
    siteUrl: src?.siteUrl ?? '',
    resourceId: src?.resourceId ?? null,
    enabled: src?.enabled ?? true,
    accessible: src?.accessible ?? true,
    displayAlias: src?.displayAlias ?? '',
    // Always one spare row, so adding a pattern never needs a click first.
    folders: [...(src?.folders ?? []), ''],
    excludeFolders: [...(src?.excludeFolders ?? []), ''],
  }
}

type SiteState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'error' }
  | { kind: 'ok'; siteId: string; displayName: string; drives: KbSharepointDrive[] }

/**
 * Create/edit one corporate source.
 *
 * The two things this form exists to make safe:
 *
 *  - the DRIVE ID is picked, never typed. A source needs a Graph drive id (`b!en8t5BARPEic…`), so
 *    the flow is: paste the site URL from a browser tab → resolve → choose from that site's
 *    libraries. Both lookups run On-Behalf-Of the admin, so only libraries they can already see
 *    are offered. (They may still be unreadable by the app itself, which crawls app-only under a
 *    Sites.Selected grant — nothing here can detect that, and the first run's failure is what
 *    surfaces it. Hence the table showing run outcomes rather than a green row on create.)
 *
 *  - a RESTRICTED source cannot be saved without an alias. Without one the citation would read
 *    `/Contrat-cadre.docx`, which looks like a path — the one thing masking exists to avoid. The
 *    server refuses it too; this just says so before the round trip, and focuses the field.
 *
 * Refusals come back as a 422 carrying the backend's own wording and are shown in place, in a
 * `role="alert"` region with `aria-invalid` on the form: they are RULES, not breakages.
 *
 * An include pattern that names no folder of the library is NOT a refusal — the save goes through
 * and the answer carries a `scopeCheck`. The dialog then stays open on that warning (with the path
 * that would match, one click away) instead of closing on a source that will index nothing: a
 * pattern written one level too high used to be found only in a job log, weeks later.
 */
export function KbSourceForm({
  source,
  onSaved,
  onChanged,
  onClose,
}: {
  /** null = create. On edit the id is shown but disabled: it is stamped on every indexed chunk. */
  source: KbSourceRecord | null
  /** Saved cleanly — the dialog is done. */
  onSaved: () => void
  /** Saved, but the dialog stays open on a scope warning — the list behind it should still refresh. */
  onChanged: () => void
  onClose: () => void
}) {
  const { t } = useLang()
  const uid = useId()
  // A create that stayed open on a scope warning has made the source real: from then on the
  // dialog EDITS it (a second POST would be refused as a duplicate id).
  const [saved, setSaved] = useState<KbSourceRecord | null>(null)
  const current = saved ?? source
  const isEdit = current !== null
  const [draft, setDraft] = useState<KbSourceDraft>(() => draftFrom(source))
  const [site, setSite] = useState<SiteState>({ kind: 'idle' })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [scopeCheck, setScopeCheck] = useState<KbScopeCheck | null>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const aliasRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    firstFieldRef.current?.focus()
    return () => prev?.focus()
  }, [])

  const set = <K extends keyof KbSourceDraft>(key: K, value: KbSourceDraft[K]): void =>
    setDraft((d) => ({ ...d, [key]: value }))

  /** Patterns are edited as repeatable rows; a blank one is dropped, never refused. */
  function setPattern(kind: 'folders' | 'excludeFolders', i: number, value: string): void {
    setDraft((d) => {
      const next = [...d[kind]]
      next[i] = value
      // Keep exactly one trailing blank, so the list always offers the next row.
      while (next.length > 0 && next[next.length - 1] === '' && next[next.length - 2] === '') {
        next.pop()
      }
      if (next[next.length - 1] !== '') next.push('')
      return { ...d, [kind]: next }
    })
  }

  function removePattern(kind: 'folders' | 'excludeFolders', i: number): void {
    setDraft((d) => {
      const next = d[kind].filter((_, j) => j !== i)
      return { ...d, [kind]: next.length > 0 ? next : [''] }
    })
  }

  /** Put the suggested path in place of the pattern it corrects. Saving again re-checks it. */
  function applySuggestion(pattern: string, suggestion: string): void {
    setDraft((d) => ({
      ...d,
      folders: d.folders.map((f) => (f.trim() === pattern ? suggestion : f)),
    }))
    setScopeCheck((c) => c && { ...c, unmatched: c.unmatched.filter((u) => u.pattern !== pattern) })
  }

  async function resolveSite(): Promise<void> {
    const url = draft.siteUrl.trim()
    if (!url) return
    setSite({ kind: 'loading' })
    try {
      const { site: found } = await api.kbResolveSite(url)
      if (!found) {
        setSite({ kind: 'none' })
        return
      }
      const { drives } = await api.kbSiteDrives(found.id)
      setSite({ kind: 'ok', siteId: found.id, displayName: found.displayName, drives })
    } catch {
      setSite({ kind: 'error' })
    }
  }

  async function save(): Promise<void> {
    // Checked here only to say WHY and focus the field that is wrong; the server refuses it
    // regardless, with wording of its own.
    if (!draft.accessible && !draft.displayAlias.trim()) {
      setError(t('kbsrc_f_alias_required'))
      aliasRef.current?.focus()
      return
    }
    setSaving(true)
    setError('')
    setScopeCheck(null)
    const folders = draft.folders.map((f) => f.trim()).filter(Boolean)
    const excludeFolders = draft.excludeFolders.map((f) => f.trim()).filter(Boolean)
    const displayAlias = draft.displayAlias.trim() || null
    try {
      const result = current
        ? await api.updateKbSource(current.id, {
            name: draft.name,
            siteUrl: draft.siteUrl,
            resourceId: draft.resourceId,
            enabled: draft.enabled,
            accessible: draft.accessible,
            displayAlias,
            folders,
            excludeFolders,
          })
        : await api.createKbSource({
            id: draft.id,
            name: draft.name,
            type: 'sharepoint_library',
            siteUrl: draft.siteUrl,
            resourceId: draft.resourceId,
            enabled: draft.enabled,
            accessible: draft.accessible,
            displayAlias,
            folders,
            excludeFolders,
          })
      const check = result.scopeCheck
      if (check && (check.unmatched.length > 0 || check.status === 'unavailable')) {
        setSaved(result)
        setScopeCheck(check)
        onChanged()
        return
      }
      onSaved()
    } catch (e) {
      // 422 = a domain rule, worded by the backend. Show it verbatim: it names the offending
      // pattern or explains why an alias is required, which no client-side paraphrase would.
      setError(isApiError(e) ? e.serverMessage || e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const title = isEdit ? t('kbsrc_form_edit_title') : t('kbsrc_form_new_title')

  const patternRows = (kind: 'folders' | 'excludeFolders'): JSX.Element => {
    const label = kind === 'folders' ? t('kbsrc_f_include') : t('kbsrc_f_exclude')
    return (
      <fieldset className="kbsrc-patterns">
        <legend>{label}</legend>
        {draft[kind].map((value, i) => (
          <div className="kbsrc-pattern-row" key={`${kind}-${i}`}>
            <input
              type="text"
              className="prompt-input"
              value={value}
              // Each row is its own control with its own accessible name — a column of unlabelled
              // text boxes is unusable without sight of the column heading.
              aria-label={t('kbsrc_f_pattern_row')
                .replace('{kind}', label)
                .replace('{n}', String(i + 1))}
              onChange={(e) => setPattern(kind, i, e.target.value)}
            />
            <button
              type="button"
              className="mini ghost"
              aria-label={t('kbsrc_f_pattern_remove').replace('{n}', String(i + 1))}
              onClick={() => removePattern(kind, i)}
            >
              ✕
            </button>
          </div>
        ))}
      </fieldset>
    )
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal kbsrc-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body kbsrc-form">
          <label className="prompt-label" htmlFor={`${uid}-id`}>
            {t('kbsrc_f_id')}
          </label>
          <input
            id={`${uid}-id`}
            ref={firstFieldRef}
            className="prompt-input"
            type="text"
            value={draft.id}
            disabled={isEdit}
            aria-describedby={`${uid}-id-hint`}
            onChange={(e) => set('id', e.target.value)}
          />
          <p className="kbsrc-hint" id={`${uid}-id-hint`}>
            {isEdit ? t('kbsrc_f_id_immutable') : t('kbsrc_f_id_hint')}
          </p>

          <label className="prompt-label" htmlFor={`${uid}-name`}>
            {t('kbsrc_f_name')}
          </label>
          <input
            id={`${uid}-name`}
            className="prompt-input"
            type="text"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
          />

          <label className="prompt-label" htmlFor={`${uid}-site`}>
            {t('kbsrc_f_site')}
          </label>
          <div className="kbsrc-pattern-row">
            <input
              id={`${uid}-site`}
              className="prompt-input"
              type="url"
              value={draft.siteUrl}
              aria-describedby={`${uid}-site-hint`}
              onChange={(e) => set('siteUrl', e.target.value)}
            />
            <button
              type="button"
              className="mini ghost"
              disabled={site.kind === 'loading' || !draft.siteUrl.trim()}
              onClick={() => void resolveSite()}
            >
              {site.kind === 'loading' ? t('kbsrc_f_resolving') : t('kbsrc_f_resolve')}
            </button>
          </div>
          <p className="kbsrc-hint" id={`${uid}-site-hint`}>
            {t('kbsrc_f_site_hint')}
          </p>
          {site.kind === 'none' && (
            <p className="kbsrc-hint kbsrc-warn" role="alert">{t('kbsrc_f_site_none')}</p>
          )}
          {site.kind === 'error' && (
            <p className="kbsrc-hint kbsrc-warn" role="alert">{t('kbsrc_f_site_error')}</p>
          )}

          {site.kind === 'ok' && (
            <>
              <label className="prompt-label" htmlFor={`${uid}-drive`}>
                {t('kbsrc_f_library')}
              </label>
              <select
                id={`${uid}-drive`}
                className="prompt-input"
                value={draft.resourceId ?? ''}
                aria-describedby={`${uid}-drive-hint`}
                onChange={(e) => set('resourceId', e.target.value || null)}
              >
                <option value="">{t('kbsrc_f_library_pick')}</option>
                {site.drives.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <p className="kbsrc-hint" id={`${uid}-drive-hint`}>
                {t('kbsrc_f_library_hint')}
              </p>
            </>
          )}

          {patternRows('folders')}
          {/* Right under the includes it is about. An alert: the save has already happened, and
              what it saved will index nothing until a pattern changes. */}
          {scopeCheck && (scopeCheck.unmatched.length > 0 || scopeCheck.status === 'unavailable') && (
            <div className="kbsrc-scope-check" role="alert">
              {scopeCheck.unmatched.length > 0 ? (
                <>
                  <p className="kbsrc-hint kbsrc-warn">{t('kbsrc_f_scope_saved_warn')}</p>
                  <ul className="kbsrc-scope-list">
                    {scopeCheck.unmatched.map((u) => (
                      <li key={u.pattern} className="kbsrc-hint">
                        {t('kbsrc_f_scope_nomatch').replace('{pattern}', u.pattern)}
                        {u.suggestion && (
                          <>
                            {' '}
                            {t('kbsrc_f_scope_suggest').replace('{path}', u.suggestion)}{' '}
                            <button
                              type="button"
                              className="mini ghost"
                              aria-label={t('kbsrc_f_scope_use_of')
                                .replace('{path}', u.suggestion)
                                .replace('{pattern}', u.pattern)}
                              onClick={() => applySuggestion(u.pattern, u.suggestion!)}
                            >
                              {t('kbsrc_f_scope_use')}
                            </button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="kbsrc-hint kbsrc-warn">{t('kbsrc_f_scope_unavailable')}</p>
              )}
            </div>
          )}
          {patternRows('excludeFolders')}
          <p className="kbsrc-hint">{t('kbsrc_f_scope_hint')}</p>
          {/* Both consequences are stated next to the controls rather than discovered later: a
              scope edit costs a full re-crawl, and narrowing one DELETES what has left the scope
              (on the next run — never in this request). */}
          <p className="kbsrc-hint kbsrc-warn">{t('kbsrc_f_warn_widen')}</p>
          <p className="kbsrc-hint kbsrc-warn">{t('kbsrc_f_warn_narrow')}</p>

          <label className="kbsrc-check" htmlFor={`${uid}-accessible`}>
            <input
              id={`${uid}-accessible`}
              type="checkbox"
              checked={draft.accessible}
              aria-describedby={`${uid}-accessible-hint`}
              onChange={(e) => {
                set('accessible', e.target.checked)
                // An alias is now required, so put the cursor where the answer goes.
                if (!e.target.checked) setTimeout(() => aliasRef.current?.focus(), 0)
              }}
            />
            {t('kbsrc_f_accessible')}
          </label>
          <p className="kbsrc-hint" id={`${uid}-accessible-hint`}>
            {t('kbsrc_f_accessible_hint')}
          </p>

          {!draft.accessible && (
            <>
              <label className="prompt-label" htmlFor={`${uid}-alias`}>
                {t('kbsrc_f_alias')}
              </label>
              <input
                id={`${uid}-alias`}
                ref={aliasRef}
                className="prompt-input"
                type="text"
                value={draft.displayAlias}
                aria-describedby={`${uid}-alias-hint`}
                onChange={(e) => set('displayAlias', e.target.value)}
              />
              <p className="kbsrc-hint" id={`${uid}-alias-hint`}>
                {t('kbsrc_f_alias_hint')}
              </p>
            </>
          )}

          <label className="kbsrc-check" htmlFor={`${uid}-enabled`}>
            <input
              id={`${uid}-enabled`}
              type="checkbox"
              checked={draft.enabled}
              aria-describedby={`${uid}-enabled-hint`}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            {t('kbsrc_f_enabled')}
          </label>
          <p className="kbsrc-hint" id={`${uid}-enabled-hint`}>
            {t('kbsrc_f_enabled_hint')}
          </p>

          {error && (
            <p className="kbsrc-hint kbsrc-error" role="alert">
              {error}
            </p>
          )}

          <div className="prompt-acts">
            {/* Once a save has gone through, there is nothing left to cancel. */}
            <button type="button" className="mini ghost" onClick={onClose}>
              {saved ? t('close') : t('cancel')}
            </button>
            <button
              type="button"
              className="mini ok"
              disabled={saving || !draft.id.trim() || !draft.name.trim()}
              aria-invalid={error ? true : undefined}
              onClick={() => void save()}
            >
              {saving ? t('kbsrc_saving') : isEdit ? t('kbsrc_save') : t('kbsrc_create')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
